import { Helmet } from 'react-helmet-async';
import * as React from 'react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Grid, Container, Typography, Box, CircularProgress, Alert, Button } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Card from '@mui/material/Card';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { getFullname, getToken, getAcl, getFirstAccessibleMenuPath } from '../utils/common';
import { AppWebsiteVisits, AppWidgetSummary } from '../sections/@dashboard/app';
import CalendarPage from './calendar/calendarcustomDashboard';
import logger from '../utils/logger';

// Helper functions to convert between English (API) and translated values
function getEnglishValue(value) {
  // Convert any translated value back to English for API/state
  if (!value) return 'Monthly'; // default
  const valueLower = value.toLowerCase();
  if (valueLower.includes('bulanan') || valueLower === 'monthly') return 'Monthly';
  if (valueLower.includes('mingguan') || valueLower === 'weekly') return 'Weekly';
  if (valueLower.includes('harian') || valueLower === 'daily') return 'Daily';
  return 'Monthly'; // default fallback
}

function getTranslatedValue(englishValue, t) {
  // Convert English value to translated value for display
  switch (englishValue) {
    case 'Monthly':
      return t('Monthly.1');
    case 'Weekly':
      return t('Weekly.1');
    case 'Daily':
      return t('Daily.1');
    default:
      return t('Monthly.1');
  }
}

// Helper function to get week for date
function getWeekForDate(dateToCheck, weekDates) {
  for (let i = 0; i < weekDates.length; i += 1) {
    const startDate = new Date(weekDates[i]);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    if (isBetween(dateToCheck, startDate, endDate)) {
      return i;
    }
  }

  return null;
}

// Helper function to get chart data based on output type
// Always use English values for comparison (API compatibility)
function getChartData(outputAuth, datasetmonthly, datasetweekly, datasetdaily) {
  // Normalize to English value for comparison
  const englishValue = getEnglishValue(outputAuth);

  // Ensure all data arrays are arrays (default to empty array if undefined)
  const monthlyData = Array.isArray(datasetmonthly) ? datasetmonthly : [];
  const weeklyData = Array.isArray(datasetweekly) ? datasetweekly : [];
  const dailyData = Array.isArray(datasetdaily) ? datasetdaily : [];

  if (englishValue === 'Monthly') {
    return [
      {
        name: 'Authorized',
        type: 'bar',
        fill: 'solid',
        data: monthlyData,
      },
    ];
  }

  if (englishValue === 'Weekly') {
    return [
      {
        name: 'Authorized',
        type: 'bar',
        fill: 'solid',
        data: weeklyData,
      },
    ];
  }

  if (englishValue === 'Daily') {
    return [
      {
        name: 'Authorized',
        type: 'bar',
        fill: 'solid',
        data: dailyData,
      },
    ];
  }

  return [
    {
      name: 'Authorized',
      type: 'bar',
      fill: 'solid',
      data: monthlyData, // Default to monthly
    },
  ];
}

// Helper function to get chart labels based on output type
// Always use English values for comparison (API compatibility)
function getChartLabels(outputAuth, monthname, weekname, dayname) {
  // Normalize to English value for comparison
  const englishValue = getEnglishValue(outputAuth);

  // Ensure all label arrays are arrays (default to empty array if undefined)
  const monthLabels = Array.isArray(monthname) ? monthname : [];
  const weekLabels = Array.isArray(weekname) ? weekname : [];
  const dayLabels = Array.isArray(dayname) ? dayname : [];

  if (englishValue === 'Monthly') {
    return monthLabels;
  }
  if (englishValue === 'Weekly') {
    return weekLabels;
  }
  if (englishValue === 'Daily') {
    return dayLabels;
  }
  return monthLabels; // Default to monthly
}

// Helper functions moved to outer scope for better performance
function convertToFormattedDate(inputString) {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const parts = inputString.split(' ');
  const monthName = parts[0];
  const year = parts[1];

  const monthIndex = monthNames.indexOf(monthName) + 1;
  const formattedMonth = String(monthIndex).padStart(2, '0');

  return `${year}-${formattedMonth}`;
}

function isBetween(checkDate, startDate, endDate) {
  return checkDate >= startDate && checkDate <= endDate;
}

function formatDateToYYYYMMDD(utcDateString) {
  const date = new Date(utcDateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function handleClickLang(lang) {
  i18next.changeLanguage(lang);
}

export default function DashboardAppPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State values are always in English for API compatibility
  const [outputProcessed, setOutputProcessed] = React.useState('Monthly');
  const [outputAuth, setOutputAuth] = React.useState('Monthly');
  const [outputunAuth, setOutputunAuth] = React.useState('Monthly');

  // Sync dropdown values when language changes
  React.useEffect(() => {
    // Ensure state values remain in English, but dropdowns will display translated text
    // No need to change state, just ensure it's valid
    if (outputAuth && getEnglishValue(outputAuth) !== outputAuth) {
      setOutputAuth(getEnglishValue(outputAuth));
    }
    if (outputunAuth && getEnglishValue(outputunAuth) !== outputunAuth) {
      setOutputunAuth(getEnglishValue(outputunAuth));
    }
    if (outputProcessed && getEnglishValue(outputProcessed) !== outputProcessed) {
      setOutputProcessed(getEnglishValue(outputProcessed));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18next.language]); // Re-run when language changes

  // Check if user has access to dashboard - redirect if not
  useEffect(() => {
    const checkAccess = () => {
      const acl = getAcl();
      if (!acl || acl === '[]' || acl === 'null' || acl === 'undefined' || acl === '') {
        return; // Wait for ACL to load
      }

      try {
        const aclJson = JSON.parse(acl);
        const hasDashboardAccess = aclJson.some((item) => item.nama_menu === 'dashboard' && item.read === true);

        if (!hasDashboardAccess) {
          const firstMenuPath = getFirstAccessibleMenuPath(acl);
          navigate(firstMenuPath, { replace: true });
        }
      } catch (error) {
        logger.error('DashboardAppPage: Error checking access:', error);
      }
    };

    // Check immediately
    checkAccess();

    // Also listen for ACL updates
    const handleAclUpdate = () => {
      checkAccess();
    };

    window.addEventListener('aclUpdated', handleAclUpdate);
    return () => {
      window.removeEventListener('aclUpdated', handleAclUpdate);
    };
  }, [navigate]);
  const [dataFraud, setdataFraud] = React.useState([]);
  const [dataNotFraud, setdataNotFraud] = React.useState([]);
  const [dataNotFraudmonthly, setdataNotFraudmonthly] = React.useState([]);
  const [dataFraudmonthly, setdataFraudmonthly] = React.useState([]);
  const [dataFraudweekly, setdataFraudweekly] = React.useState([]);
  const [dataNotFraudweekly, setdataNotFraudweekly] = React.useState([]);
  const [dayname, setdayname] = React.useState([]);
  const [monthname, setmothname] = React.useState([]);
  const [weekname, setweekname] = React.useState([]);
  const [datasetdaily, setdatasetdaily] = React.useState([]);
  const [datasetmonthly, setdatasetmonthly] = React.useState([]);
  const [datasetweekly, setdatasetweekly] = React.useState();

  const [datasetdaily2, setdatasetdaily2] = React.useState([]);
  const [datasetmonthly2, setdatasetmonthly2] = React.useState([]);
  const [datasetweekly2, setdatasetweekly2] = React.useState();
  const [user] = React.useState(getFullname());
  const [dataTotal, setDataTotal] = React.useState({
    open: 0,
    inProgress: 0,
    penddingApproval: 0,
    penddingRollback: 0,
  });

  // Add loading and error states
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const handleChangeAuth = (event) => {
    // Convert translated value to English for state (API compatibility)
    const englishValue = getEnglishValue(event.target.value);
    setOutputAuth(englishValue);
  };
  const handleChangeunAuth = (event) => {
    // Convert translated value to English for state (API compatibility)
    const englishValue = getEnglishValue(event.target.value);
    setOutputunAuth(englishValue);
  };
  const handleChangeProcessed = (event) => {
    // Convert translated value to English for state (API compatibility)
    const englishValue = getEnglishValue(event.target.value);
    setOutputProcessed(englishValue);
  };

  const urlpiechart = `${globalThis.REACT_APP_API_PURE}dashboard/list?type=1`;

  const fetchInfo2 = () =>
    fetch(urlpiechart, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        const data = Array.isArray(d) ? d : [];
        setDataTotal({
          ...dataTotal,
          open: Number(data.filter((val) => val?.status === 'open')[0]?.total) || 0,
          inProgress: Number(data.filter((val) => val?.status === 'inProgress')[0]?.total) || 0,
          penddingApproval: Number(data.filter((val) => val?.status === 'pendingApproval')[0]?.total) || 0,
          penddingRollback: Number(data.filter((val) => val?.status === 'pendingRollback')[0]?.total) || 0,
        });
      })
      .catch((error) => {
        logger.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again.');
      });

  const fetchInfo3 = (url, set1, set2, sevenDaysBeforeDates = []) =>
    fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        const data = Array.isArray(d) ? d : [];
        setdata(url, 'rollback', data, set1, sevenDaysBeforeDates);
        setdata(url, 'keep', data, set2, sevenDaysBeforeDates);
      })
      .catch((error) => {
        logger.error('Error in fetchInfo3:', error);
        setdata(url, 'rollback', [], set1, sevenDaysBeforeDates);
        setdata(url, 'keep', [], set2, sevenDaysBeforeDates);
      });
  const fetchInfo4 = (url, set1, sevenDaysBeforeDates = []) =>
    fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        const data = Array.isArray(d) ? d : [];
        setdata2(
          url,
          data.filter((val) => val?.status === 'authorized'),
          set1,
          sevenDaysBeforeDates
        );
      })
      .catch((error) => {
        logger.error('Error in fetchInfo4:', error);
        setdata2(url, [], set1, sevenDaysBeforeDates);
      });
  const fetchInfo5 = (url, set1, sevenDaysBeforeDates = []) =>
    fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        const data = Array.isArray(d) ? d : [];
        setdata2(
          url,
          data.filter((val) => val?.status === 'unauthorized'),
          set1,
          sevenDaysBeforeDates
        );
      })
      .catch((error) => {
        logger.error('Error in fetchInfo5:', error);
        setdata2(url, [], set1, sevenDaysBeforeDates);
      });

  const setdata2 = (url, data, set, sevenDaysBeforeDates) => {
    const removepath = data || [];
    if (url === `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&duration=daily`) {
      const intArray = sevenDaysBeforeDates.map((obj, _i) => {
        const remo = removepath?.filter((objj) => formatDateToYYYYMMDD(objj.date) === obj.date);
        if (remo.length !== 0) {
          return Number.parseInt(remo[0].total, 10);
        }
        return 0;
      });
      set(intArray);
    } else if (url === `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&status=Fraud&duration=monthly`) {
      const intArray = sevenDaysBeforeDates.map((obj, _i) => {
        const remo = removepath?.filter((objj) => objj.date === convertToFormattedDate(obj));
        if (remo.length !== 0) {
          return Number.parseInt(remo[0].total, 10);
        }
        return 0;
      });
      set(intArray);
    } else if (url === `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&duration=weekly`) {
      let number1 = 0;
      let number2 = 0;
      let number3 = 0;
      let number4 = 0;

      const weeklyTotals = [0, 0, 0, 0];
      removepath.forEach((obj) => {
        const log = getWeekForDate(new Date(formatDateToYYYYMMDD(obj.date)), sevenDaysBeforeDates);
        if (log >= 0 && log < 4) {
          weeklyTotals[log] += Number.parseInt(obj.total, 10);
        }
      });
      [number1, number2, number3, number4] = weeklyTotals;
      set([number1, number2, number3, number4]);
    } else {
      const intArray = (removepath || []).map((obj, _i) => Number.parseInt(obj.total, 10));

      set(intArray);
    }
  };

  const setdata = (url, tittle, data, set, sevenDaysBeforeDates) => {
    const dataArray = Array.isArray(data) ? data : [];
    const removepath = dataArray.filter((val) => val?.status === tittle);
    if (url === `${globalThis.REACT_APP_API_PURE}dashboard/list?type=2&duration=daily`) {
      const intArray = sevenDaysBeforeDates?.map((obj) => {
        const remo = removepath?.filter((objj) => formatDateToYYYYMMDD(objj.date) === obj.date);
        if (remo?.length !== 0) {
          return Number.parseInt(remo?.[0]?.total, 10);
        }
        return 0;
      });
      set(intArray);
    } else if (url === `${globalThis.REACT_APP_API_PURE}dashboard/list?type=2&duration=monthly`) {
      const intArray = sevenDaysBeforeDates.map((obj, _i) => {
        const remo = removepath?.filter((objj) => objj.date === convertToFormattedDate(obj));
        if (remo?.length !== 0) {
          return Number.parseInt(remo?.[0]?.total, 10);
        }
        return 0;
      });
      set(intArray);
    } else if (url === `${globalThis.REACT_APP_API_PURE}dashboard/list?type=2&duration=daily?limit=1000`) {
      let number1 = 0;
      let number2 = 0;
      let number3 = 0;
      let number4 = 0;

      removepath?.map((obj) => {
        const log = getWeekForDate(new Date(formatDateToYYYYMMDD(obj.date)), sevenDaysBeforeDates);
        if (log === 0) {
          number1 += Number.parseInt(obj.total, 10);
        } else if (log === 1) {
          number2 += Number.parseInt(obj.total, 10);
        } else if (log === 2) {
          number3 += Number.parseInt(obj.total, 10);
        } else if (log === 3) {
          number4 += Number.parseInt(obj.total, 10);
        }
        return obj;
      });

      set([number1, number2, number3, number4]);
    } else {
      const intArray = (removepath || []).map((obj, _i) => Number.parseInt(obj.total, 10));

      set(intArray);
    }
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load all dashboard data
        await Promise.all([
          fetchInfo2(),
          // Add other fetch calls here if needed
        ]);

        setIsLoading(false);
      } catch (error) {
        logger.error('Error loading dashboard:', error);
        setError('Failed to load dashboard. Please refresh the page.');
        setIsLoading(false);
      }
    };

    loadDashboardData();

    const currentDate = new Date();
    const daysBefore = 7;
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const sevenDaysBeforeDates = [];
    const monthNamesArray = [];

    for (let i = daysBefore - 1; i >= 0; i -= 1) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - i);
      const dayName = daysOfWeek[date.getDay()];
      const formattedDate = date.toISOString().slice(0, 10);
      sevenDaysBeforeDates.push({ date: formattedDate, day: dayName });
      setdayname((prevArray) => [...prevArray, dayName + formattedDate.slice(4).replace('-', '/')]);
      const monthIndex = (currentMonth - i + 12) % 12;
      const monthName = monthNames[monthIndex];
      const year = monthIndex <= currentMonth ? currentYear : currentYear - 1;
      const formattedMonthYear = `${monthName} ${year}`;
      monthNamesArray.push(formattedMonthYear);
      setmothname((prevArray) => [...prevArray, formattedMonthYear]);
    }

    const weeksBefore = 4;
    const weekDates = [];

    for (let i = weeksBefore - 1; i >= 0; i -= 1) {
      const date = new Date();
      date.setDate(date.getDate() - i * 7);

      while (date.getDay() !== 1) {
        date.setDate(date.getDate() - 1);
      }

      const formattedDate = formatDate(date);
      const weekLabel = `${formattedDate}`;
      weekDates.push(weekLabel);
      setweekname((prevArray) => [...prevArray, weekLabel]);
    }

    fetchInfo3(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=2&duration=daily`,
      setdataFraud,
      setdataNotFraud,
      sevenDaysBeforeDates
    );
    fetchInfo3(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=2&duration=monthly`,
      setdataFraudmonthly,
      setdataNotFraudmonthly,
      monthNamesArray
    );
    fetchInfo3(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=2&duration=daily?limit=1000`,
      setdataFraudweekly,
      setdataNotFraudweekly,
      weekDates
    );

    fetchInfo4(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&duration=daily`,
      setdatasetdaily,
      sevenDaysBeforeDates
    );
    fetchInfo4(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&status=Fraud&duration=monthly`,
      setdatasetmonthly,
      monthNamesArray
    );
    fetchInfo4(`${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&duration=weekly`, setdatasetweekly, weekDates);

    fetchInfo5(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&duration=daily`,
      setdatasetdaily2,
      sevenDaysBeforeDates
    );
    fetchInfo5(
      `${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&status=Fraud&duration=monthly`,
      setdatasetmonthly2,
      monthNamesArray
    );
    fetchInfo5(`${globalThis.REACT_APP_API_PURE}dashboard/list?type=3&duration=weekly`, setdatasetweekly2, weekDates);
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Dashboard | SMARTSHIELD</title>
        </Helmet>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Loading dashboard...
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <Helmet>
          <title>Dashboard | SMARTSHIELD</title>
        </Helmet>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Error Loading Dashboard
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | SMARTSHIELD</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography component="h1" variant="h4" sx={{ mb: 5 }}>
          Hi, {t('Welcome.1')} {user || ''}
        </Typography>
        <div className="flex flex-col space-y-5 ...">
          <div className="grid grid-rows-3 grid-flow-col gap-5">
            <div className="row-start-1 row-end-4 pt-4 ...">
              <Grid container spacing={3} justify="center" alignItems="center">
                <Grid item xs={2} sm={2} md={3}>
                  <Link
                    to={{
                      pathname: `/dashboard/alert`,
                      search: `?id=${0}`,
                    }}
                    state={{ index: 0 }}
                    aria-label={`Open alerts: ${dataTotal.open}`}
                  >
                    <AppWidgetSummary
                      title="Open"
                      total={dataTotal.open}
                      color="error"
                      icon={'ant-design:android-filled'}
                    />
                  </Link>
                </Grid>

                <Grid item xs={2} sm={2} md={3}>
                  <Link
                    to={{
                      pathname: `/dashboard/alert`,
                      search: `?id=${1}`,
                    }}
                    state={{ index: 1 }}
                    aria-label={`In Progress alerts: ${dataTotal.inProgress}`}
                  >
                    <AppWidgetSummary
                      title="In Progress"
                      total={dataTotal.inProgress}
                      color="warning"
                      icon={'ant-design:apple-filled'}
                    />
                  </Link>
                </Grid>
                <Grid item xs={6} sm={2} md={3}>
                  <Link
                    to={{
                      pathname: `/dashboard/alert`,
                      search: `?id=${2}`,
                    }}
                    state={{ index: 2 }}
                    aria-label={`Pending Approval alerts: ${dataTotal.penddingApproval}`}
                  >
                    <AppWidgetSummary
                      title="Pending Approval"
                      total={dataTotal.penddingApproval}
                      color="info"
                      icon={'ant-design:windows-filled'}
                    />
                  </Link>
                </Grid>

                <Grid item xs={6} sm={2} md={3}>
                  <Link
                    to={{
                      pathname: `/dashboard/alert`,
                      search: `?id=${3}`,
                    }}
                    state={{ index: 3 }}
                    aria-label={`Pending Rollback alerts: ${dataTotal.penddingRollback}`}
                  >
                    <AppWidgetSummary
                      title="Pending Rollback"
                      total={dataTotal.penddingRollback}
                      icon={'ant-design:bug-filled'}
                    />
                  </Link>
                </Grid>
              </Grid>
            </div>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3} lg={6}>
              <Card>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel id="demo-select-small-label-auth">{t('TimeSpan.1')}</InputLabel>
                  <Select
                    labelId="demo-select-small-label-auth"
                    id="demo-select-small-auth"
                    value={outputAuth}
                    label="Time Span"
                    onChange={handleChangeAuth}
                  >
                    <MenuItem value="Monthly">{t('Monthly.1')}</MenuItem>
                    <MenuItem value="Weekly">{t('Weekly.1')}</MenuItem>
                    <MenuItem value="Daily">{t('Daily.1')}</MenuItem>
                  </Select>
                </FormControl>
                {}
                {}
                <AppWebsiteVisits
                  title={t('authorizedUpdates.1')}
                  subheader={getTranslatedValue(outputAuth, t)}
                  chartLabels={getChartLabels(outputAuth, monthname, weekname, dayname)}
                  chartData={getChartData(outputAuth, datasetmonthly, datasetweekly, datasetdaily)}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={3} lg={6}>
              <Card>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel id="demo-select-small-label-unauth">{t('TimeSpan.1')}</InputLabel>
                  <Select
                    labelId="demo-select-small-label-unauth"
                    id="demo-select-small-unauth"
                    value={outputunAuth}
                    label="Time Span"
                    onChange={handleChangeunAuth}
                  >
                    <MenuItem value="Monthly">{t('Monthly.1')}</MenuItem>
                    <MenuItem value="Weekly">{t('Weekly.1')}</MenuItem>
                    <MenuItem value="Daily">{t('Daily.1')}</MenuItem>
                  </Select>
                </FormControl>
                {}
                {}
                <AppWebsiteVisits
                  title={t('unauthorizedUpdates.1')}
                  subheader={getTranslatedValue(outputunAuth, t)}
                  chartLabels={getChartLabels(outputunAuth, monthname, weekname, dayname)}
                  chartData={(() => {
                    // Normalize to English value for comparison
                    const englishValue = getEnglishValue(outputunAuth);
                    // Ensure data arrays are always arrays
                    const monthlyData = Array.isArray(datasetmonthly2) ? datasetmonthly2 : [];
                    const weeklyData = Array.isArray(datasetweekly2) ? datasetweekly2 : [];
                    const dailyData = Array.isArray(datasetdaily2) ? datasetdaily2 : [];

                    if (englishValue === 'Monthly') {
                      return [
                        {
                          name: 'Unauthorized',
                          type: 'bar',
                          fill: 'solid',
                          data: monthlyData,
                        },
                      ];
                    }
                    if (englishValue === 'Weekly') {
                      return [
                        {
                          name: 'Unauthorized',
                          type: 'bar',
                          fill: 'solid',
                          data: weeklyData,
                        },
                      ];
                    }
                    if (englishValue === 'Daily') {
                      return [
                        {
                          name: 'Unauthorized',
                          type: 'bar',
                          fill: 'solid',
                          data: dailyData,
                        },
                      ];
                    }
                    return [
                      {
                        name: 'Unauthorized',
                        type: 'bar',
                        fill: 'solid',
                        data: monthlyData,
                      },
                    ];
                  })()}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={3} lg={6}>
              <Card>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel id="demo-select-small-label-processed">{t('TimeSpan.1')}</InputLabel>
                  <Select
                    labelId="demo-select-small-label-processed"
                    id="demo-select-small-processed"
                    value={outputProcessed}
                    label="Time Span"
                    onChange={handleChangeProcessed}
                  >
                    <MenuItem value="Monthly">{t('Monthly.1')}</MenuItem>
                    <MenuItem value="Weekly">{t('Weekly.1')}</MenuItem>
                    <MenuItem value="Daily">{t('Daily.1')}</MenuItem>
                  </Select>
                </FormControl>
                {}
                {}
                <AppWebsiteVisits
                  title={t('Processed.1')}
                  subheader={getTranslatedValue(outputProcessed, t)}
                  chartLabels={getChartLabels(outputProcessed, monthname, weekname, dayname)}
                  chartData={(() => {
                    // Normalize to English value for comparison
                    const englishValue = getEnglishValue(outputProcessed);
                    // Ensure data arrays are always arrays
                    const monthlyFraud = Array.isArray(dataFraudmonthly) ? dataFraudmonthly : [];
                    const monthlyNotFraud = Array.isArray(dataNotFraudmonthly) ? dataNotFraudmonthly : [];
                    const weeklyFraud = Array.isArray(dataFraudweekly) ? dataFraudweekly : [];
                    const weeklyNotFraud = Array.isArray(dataNotFraudweekly) ? dataNotFraudweekly : [];
                    const dailyFraud = Array.isArray(dataFraud) ? dataFraud : [];
                    const dailyNotFraud = Array.isArray(dataNotFraud) ? dataNotFraud : [];

                    if (englishValue === 'Monthly') {
                      return [
                        {
                          name: 'Rollback',
                          type: 'bar',
                          fill: 'solid',
                          data: monthlyFraud,
                        },
                        {
                          name: 'Keep',
                          type: 'bar',
                          fill: 'solid',
                          data: monthlyNotFraud,
                        },
                      ];
                    }
                    if (englishValue === 'Weekly') {
                      return [
                        {
                          name: 'Rollback',
                          type: 'bar',
                          fill: 'solid',
                          data: weeklyFraud,
                        },
                        {
                          name: 'Keep',
                          type: 'bar',
                          fill: 'solid',
                          data: weeklyNotFraud,
                        },
                      ];
                    }
                    if (englishValue === 'Daily') {
                      return [
                        {
                          name: 'Rollback',
                          type: 'bar',
                          fill: 'solid',
                          data: dailyFraud,
                        },
                        {
                          name: 'Keep',
                          type: 'bar',
                          fill: 'solid',
                          data: dailyNotFraud,
                        },
                      ];
                    }
                    return [
                      {
                        name: 'Rollback',
                        type: 'bar',
                        fill: 'solid',
                        data: monthlyFraud,
                      },
                      {
                        name: 'Keep',
                        type: 'bar',
                        fill: 'solid',
                        data: monthlyNotFraud,
                      },
                    ];
                  })()}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={3} lg={6}>
              <Card>
                <CalendarPage />
              </Card>
            </Grid>
          </Grid>
          {}

          {}
        </div>
      </Container>
    </>
  );
}
